import { FC } from 'react'
import { Link } from 'gatsby'
import { css } from '@emotion/react'
import { MyTheme, theme } from '@ui/styles/theme'
import newsCategoryColor from '../../../src/core/hooks/newsCategoryColor'

import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { buildLangPath } from '@lib/pathUtils'
import { Languages } from '@/types/languages'

type NewsType = {
  news:
    | Queries.NewsSummaryFragment[]
    | Queries.NewsEnSummaryFragment[]
    | Queries.NewsThSummaryFragment[]
  isNewsPage?: boolean
}

const getNewsIdInLanguage = (
  language: Languages,
  news:
    | Queries.NewsSummaryFragment
    | Queries.NewsEnSummaryFragment
    | Queries.NewsThSummaryFragment
) => {
  switch (language) {
    case 'ja':
      return (news as Queries.NewsSummaryFragment).newsId
    case 'en':
      return (news as Queries.NewsEnSummaryFragment).newsEnId
    case 'th':
      return (news as Queries.NewsThSummaryFragment).newsThId
    default: {
      const _: never = language
      throw new Error('unreachable!')
    }
  }
}

const NewsList: FC<NewsType> = ({ news, isNewsPage = false }) => {
  const styles = createStyles(theme, isNewsPage)
  const { i18n } = useTranslation()

  return (
    <ul css={styles.newsList}>
      {news.map(n => {
        const idOfNews = getNewsIdInLanguage(i18n.language as Languages, n)
        const path = buildLangPath(`/news/${idOfNews}/`, i18n.language)

        return (
          <li css={styles.newsListItem} key={idOfNews}>
            <Link to={path}>
              <span css={styles.newsListItemDate}>
                {dayjs(n.modifiedPublishedAt).format('YYYY/MM/DD')}
              </span>
              {n.category && (
                <span css={styles.newsListItemCategoryContainer}>
                  <span
                    css={styles.newsListItemCategoryInner}
                    style={{
                      backgroundColor: newsCategoryColor(n.category[0])
                    }}
                  >
                    {n.category[0]}
                  </span>
                </span>
              )}
              <span css={styles.newsListItemTitle}>{n.title}</span>
            </Link>
          </li>
        )
      })}
    </ul>
  )
}

const createStyles = (theme: MyTheme, isNewsPage: boolean) => {
  return {
    newsList: css`
      ${theme.mq.isTablet} {
        width: 100%;
      }
    `,
    newsListItem: css`
      margin-bottom: 28px;
      a {
        width: 100%;
        color: ${theme.colors.textBlack};
        text-decoration: none;
        &:hover {
          color: ${theme.colors.primaryDark};
          text-decoration: underline ${theme.colors.primaryDark};
          text-underline-offset: 0.4em;
        }
      }
    `,
    newsListItemDate: css`
      position: relative;
      display: inline-block;
      margin-right: 20px;
      font-family: 'montserrat', sans-serif;
      font-size: 16px;
      line-height: 24px;
      ${theme.mq.isTablet} {
        margin-right: 30px;
      }
    `,
    newsListItemCategoryContainer: css`
      ${theme.mq.isDesktop} {
        display: inline-block;
        min-width: 144px;
      }
    `,
    newsListItemCategoryInner: css`
      position: relative;
      display: inline-block;
      border-radius: 3px;
      padding: 0 6px;
      color: ${theme.colors.white};
      font-size: 14px;
      line-height: 24px;
    `,
    newsListItemTitle: css`
      display: block;
      margin: 8px 0;
      font-size: 16px;
      font-weight: ${isNewsPage ? 'bold' : 'normal'};
      line-height: 24px;
      ${theme.mq.isDesktop} {
        display: ${isNewsPage ? 'block' : 'inline-block'};
      }
    `
  }
}

export default NewsList
